import axios from 'axios';
import config from '../config';

// Используем конфигурацию из config.js (без .env файлов)
export const api = axios.create({
  baseURL: config.API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 секунд
});

// Логирование запросов в dev режиме
if (config.debug) {
  console.log('🌐 API initialized:', config.API_BASE);
}

// Добавляем interceptor для автоматической вставки токена и company ID
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add Company ID header for multi-tenancy
    const currentCompany = localStorage.getItem('current_company');
    if (currentCompany) {
      try {
        const company = JSON.parse(currentCompany);
        if (company && company.id) {
          config.headers['X-Company-ID'] = company.id;
        }
      } catch (e) {
        console.error('Failed to parse current company', e);
      }
    }
    
    // Add User ID header
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        if (userData && userData.id) {
          config.headers['X-User-ID'] = userData.id;
        }
      } catch (e) {
        console.error('Failed to parse user', e);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Добавляем interceptor для обработки ошибок авторизации
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Токен истек или невалидный - перенаправляем на логин
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Клиенты
export const clientsApi = {
  getAll: (params = {}) => api.get('/clients/', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients/', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Проекты
export const projectsApi = {
  getAll: (params = {}) => api.get('/projects/', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects/', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getByStatus: (status) => api.get(`/projects/by-status/${status}`),
};

// Услуги
export const servicesApi = {
  getAll: (params = {}) => api.get('/services/', { params }),
  getById: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services/', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  getCategories: () => api.get('/services/categories/'),
};

// Счета
export const invoicesApi = {
  getAll: (params = {}) => api.get('/invoices/', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices/', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  getOverdue: () => api.get('/invoices/overdue/list'),
};

// Платежи
export const paymentsApi = {
  getAll: (params = {}) => api.get('/payments/', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments/', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  getOverdue: () => api.get('/payments/overdue/list'),
  markReceived: (id) => api.post(`/payments/${id}/mark-received`),
};

// График платежей
export const paymentSchedulesApi = {
  getAll: (params = {}) => api.get('/payment-schedules/', { params }),
  getById: (id) => api.get(`/payment-schedules/${id}`),
  create: (data) => api.post('/payment-schedules/', data),
  update: (id, data) => api.put(`/payment-schedules/${id}`, data),
  delete: (id) => api.delete(`/payment-schedules/${id}`),
  createInvoice: (id) => api.post(`/payment-schedules/${id}/create-invoice`),
  getSummary: (year) => api.get('/payment-schedules/summary/by-month', { params: { year } }),
};

// Расходы (Financial Flow) — этот роутер без завершающего слеша в путях
export const expensesApi = {
  getAll: (params = {}) => api.get('/financial-flow/records', { params }),
  getById: (id) => api.get(`/financial-flow/records/${id}`),
  create: (data) => api.post('/financial-flow/records', data),
  update: (id, data) => api.put(`/financial-flow/records/${id}`, data),
  delete: (id) => api.delete(`/financial-flow/records/${id}`),
  markPaid: (id) => api.post(`/financial-flow/records/${id}/mark-paid`),
  getCategories: () => api.get('/financial-flow/records/categories'),
  getSummary: (params = {}) => api.get('/financial-flow/summary', { params }),
  getTimeseries: (params = {}) => api.get('/financial-flow/summary/timeseries', { params }),
  importBankStatement: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/financial-flow/records/import-bank-statement', formData, MULTIPART_HEADERS);
  },
};

// Отчеты
export const reportsApi = {
  getDashboard: () => api.get('/reports/dashboard'),
  getMonthlyRevenue: (year = new Date().getFullYear()) => api.get('/reports/monthly-revenue', { params: { year } }),
  getProjectDistribution: () => api.get('/reports/project-status-distribution'),
  getClientRevenue: (params = {}) => api.get('/reports/client-revenue', { params }),
  getOverdueSummary: () => api.get('/reports/overdue-summary'),
  getProjectsList: () => api.get('/projects/', { params: { limit: 100 } }).then(res => res.data?.projects || []),
  getByManager: (manager, dateFrom, dateTo) => api.get(`/reports/by-manager/${encodeURIComponent(manager)}`, { params: { date_from: dateFrom, date_to: dateTo } }).then(res => res.data),
  getProjectReport: (projectId, dateFrom, dateTo) => api.get(`/reports/project/${projectId}`, { params: { date_from: dateFrom, date_to: dateTo } }).then(res => res.data),
  getSummary: (dateFrom, dateTo) => api.get('/reports/summary', { params: { date_from: dateFrom, date_to: dateTo } }).then(res => res.data),
  getChurnAnalysis: (period, months) => api.get('/reports/churn-analysis', { params: { period, months } }).then(res => res.data),
  getSchedule: () => api.get('/reports/schedule'),
  saveSchedule: (data) => api.put('/reports/schedule', data),
};

// Файлы
export const filesApi = {
  upload: (projectId, formData) => api.post(`/files/upload/${projectId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getProjectFiles: (projectId, category) => api.get(`/files/project/${projectId}`, { params: { category } }),
  deleteFile: (id) => api.delete(`/files/${id}`),
};

// Telegram
export const telegramApi = {
  test: () => api.get('/telegram/test'),
  notifyOverdue: () => api.post('/telegram/notify-overdue'),
  sendDailyReport: () => api.post('/telegram/daily-report'),
};

export const bitrixApi = {
  sendDailyReport: () => api.post('/bitrix/daily-report'),
};

// Companies (Multi-tenancy)
export const companiesApi = {
  getMy: () => api.get('/companies/my'),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  getStats: (id) => api.get(`/companies/${id}/stats`),
};

// Team Management
export const teamApi = {
  getMembers: (companyId) => api.get(`/companies/${companyId}/team`),
  inviteUser: (companyId, data) => api.post(`/companies/${companyId}/team/invite`, data),
  updateRole: (companyId, userId, data) => api.put(`/companies/${companyId}/team/${userId}/role`, data),
  updatePermissions: (companyId, userId, data) => api.put(`/companies/${companyId}/team/${userId}/permissions`, data),
  removeUser: (companyId, userId) => api.delete(`/companies/${companyId}/team/${userId}`),
};

export const teamLoadApi = {
  getSummary: () => api.get('/team-load/summary'),
};

// Auth
export const authApi = {
  me: () => api.get('/auth/me'),
};

/**
 * Общий шаг после получения access_token (и через Яндекс, и через запасной вход по email):
 * сохраняет пользователя, определяет компанию и решает, куда вести дальше.
 * Используется в AuthCallback.jsx и в Login.jsx (запасной вход).
 */
export const completeLogin = async (token, { selectCompany }) => {
  localStorage.setItem('access_token', token);

  const meResponse = await authApi.me();
  localStorage.setItem('user', JSON.stringify(meResponse.data));

  const companiesResponse = await companiesApi.getMy();
  const companies = companiesResponse.data.companies || [];

  // Сервисный аккаунт платформы: его дом — сводка по продукту, а не CRM
  // конкретной компании, даже если формально он состоит в одной из них
  // (нужно для тестирования фич без имперсонации — см. require_plan_feature).
  if (meResponse.data.is_platform_admin) {
    return '/admin/companies';
  }

  if (companies.length === 1) {
    selectCompany(companies[0]);
    return '/dashboard';
  }
  return '/companies';
};

// Биллинг
export const billingApi = {
  getPlans: () => api.get('/billing/plans'),
  getSubscription: () => api.get('/billing/subscription'),
  checkout: (data) => api.post('/billing/checkout', data),
  cancelSubscription: () => api.post('/billing/subscription/cancel'),
  reactivateSubscription: () => api.post('/billing/subscription/reactivate'),
  getUsage: () => api.get('/billing/usage'),
  getTransactions: () => api.get('/billing/transactions'),
  getInvoices: () => api.get('/billing/invoices'),
};

// Платформенная админ-панель (только для команды Data Metrics)
export const adminApi = {
  getCompanies: () => api.get('/admin/companies'),
  getOverview: () => api.get('/admin/overview'),
  impersonate: (companyId) => api.post(`/admin/companies/${companyId}/impersonate`),
  getAuditLog: () => api.get('/admin/audit-log'),
};

// Коннекторы (интеграции с внешними системами)
export const integrationsApi = {
  getTelegram: () => api.get('/integrations/telegram'),
  saveTelegram: (data) => api.put('/integrations/telegram', data),
  testTelegram: (data) => api.post('/integrations/telegram/test', data || {}),
  deleteTelegram: () => api.delete('/integrations/telegram'),
  getBitrix: () => api.get('/integrations/bitrix'),
  saveBitrix: (data) => api.put('/integrations/bitrix', data),
  testBitrix: () => api.post('/integrations/bitrix/test'),
  deleteBitrix: () => api.delete('/integrations/bitrix'),
  getBitrixConnectUrl: () => api.get('/bitrix/oauth/connect'),
};

// Загрузка счетов/платёжек с автораспознаванием (тариф Professional+)
//
// У инстанса `api` по умолчанию стоит Content-Type: application/json (см. выше).
// Для этих запросов его нужно явно СБРОСИТЬ (в undefined, не убрать вовсе —
// иначе дефолт инстанса перекрывает FormData) — тогда браузер сам поставит
// "multipart/form-data; boundary=...". Без этого сервер получает тело как бы
// без границы и падает то с "Missing boundary", то с "files: field required",
// в зависимости от того, что именно axios успел отправить.
const MULTIPART_HEADERS = { headers: { 'Content-Type': undefined } };

export const documentsApi = {
  uploadInvoice: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/documents/upload-invoice', formData, MULTIPART_HEADERS);
  },
  uploadPayment: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/documents/upload-payment', formData, MULTIPART_HEADERS);
  },
  uploadInvoicesBulk: (files) => {
    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append('files', f));
    return api.post('/documents/upload-invoices-bulk', formData, MULTIPART_HEADERS);
  },
  uploadPaymentsBulk: (files) => {
    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append('files', f));
    return api.post('/documents/upload-payments-bulk', formData, MULTIPART_HEADERS);
  },
};
